import { InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { isNil, noop } from 'lodash/fp';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type * as api from '../../../api';
import { useThemeComponents } from '../../../themes';

export type MaintenanceSettings = Omit<api.MaintenanceUpdateFragment, '__typename'>;

type MaintenanceWatcherProps = {
    company: api.CompanyContextDataFragment;
    setMaintenancePageContent: (maintenance: MaintenanceSettings) => unknown;
    setWatcherMounted: (mounted: boolean) => unknown;
};

type Timer = ReturnType<typeof setTimeout>;

const MaintenanceWatcher = ({ setMaintenancePageContent, setWatcherMounted, company }: MaintenanceWatcherProps) => {
    const [maintenance, setMaintenance] = useState(company.activeMaintenanceModule);
    const { t } = useTranslation('common');
    const { notification } = useThemeComponents();

    useEffect(() => {
        setWatcherMounted(true);

        return () => {
            setWatcherMounted(false);
        };
    }, [setWatcherMounted]);

    // subscribe to maintenance update message
    useEffect(() => {
        const token = PubSub.subscribe(`message.${company.id}.MaintenanceUpdate`, (message, data) => {
            setMaintenance(data);
        });

        return () => {
            PubSub.unsubscribe(token);
        };
    }, [company.id, setMaintenance]);

    // update internal state if maintenance on company updates
    useEffect(() => {
        setMaintenance(company.activeMaintenanceModule);
    }, [company.activeMaintenanceModule, setMaintenance]);

    const [warning, setWarning] = useState<string | null>(null);
    const initialWarningDismissed = useMemo(
        () => sessionStorage.getItem(`maintenance-warning-dismissed-${company.id}`) === 'true',
        [company.id]
    );
    const [warningDismissed, setWarningDismissed] = useState(initialWarningDismissed);

    useEffect(() => {
        if (isNil(maintenance) || !maintenance.isActive) {
            // remove warning and maintenance page
            setWarning(null);
            setMaintenancePageContent(null);

            return noop;
        }

        const { period, warningBefore } = maintenance;

        const now = dayjs();
        const start = dayjs(period.start);
        const end = dayjs(period.end);

        // maintenance finished
        if (now.isAfter(end)) {
            // remove warning and maintenance page
            setWarning(null);
            setMaintenancePageContent(null);

            return noop;
        }

        // maintenance started
        if (now.isAfter(start)) {
            setWarning(null);
            setMaintenancePageContent(maintenance);

            return noop;
        }

        // maintenance is not started yet
        let warningTimer: Timer | undefined;
        if (warningBefore > 0) {
            const warningAt = start.add(warningBefore * -1, 'second');

            const day = start.format('DD MMM YYYY');
            const time = start.format('hh:mm a');
            warningTimer = setTimeout(
                () => setWarning(t('common:messages.maintenanceWarning', { day, time })),
                warningAt.diff(now, 'second') * 1000
            );
        }

        const maintenanceTimer = setTimeout(
            () => setMaintenancePageContent(maintenance),
            start.diff(now, 'second') * 1000
        );

        return () => {
            if (warningTimer) {
                clearTimeout(warningTimer);
            }

            clearTimeout(maintenanceTimer);
        };
    }, [maintenance, setMaintenancePageContent, setWarning, t]);

    useEffect(() => {
        let dismiss = () => {
            sessionStorage.setItem(`maintenance-warning-dismissed-${company.id}`, 'true');
            setWarningDismissed(true);
        };

        if (!warningDismissed && warning) {
            notification.error({
                content: warning,
                onClose() {
                    dismiss();
                },
                duration: 0,
                icon: <InfoCircleOutlined style={{ color: 'red' }} />,
                style: {
                    backgroundColor: '#FDEAE9',
                    width: 'auto',
                },
                placement: 'bottom',
            });
        }

        return () => {
            dismiss = noop;
        };
    }, [warning, warningDismissed, company.id, notification]);

    return null;
};

export default MaintenanceWatcher;
